import React from 'react'
import { clone } from 'ramda'
import { StickyContainer, Sticky } from 'react-sticky'
import Quote from '../components/quote'
import Spinner from '../components/spinner'
import JournalExportModal from '../components/journal-export-modal'
import AdditionalThoughtsModal from '../components/additional-thoughts-modal'
import ReflectionForm from '../components/reflection-form'
import { createRoot } from 'react-dom/client'
import { ApolloProvider, useQuery } from '@apollo/client'
import client from '../client'
import fetchReflection from '../queries/reflection'
import { dateFromISO8601, formatDate } from '../utils'
import { sort } from 'ramda'
import PostscriptText from '../components/postscript-text'

const formattedTime = (timestamp) => {
  return formatDate(timestamp, "EE, MMMM d 'at' h:mma")
}

const byDueAtAsc = (a, b) => {
  return a.dueAt && b.dueAt
    ? dateFromISO8601(a.dueAt) - dateFromISO8601(b.dueAt)
    : 0
}

const copyToClipboard = (text) => {
  if (window.clipboardData && window.clipboardData.setData) {
    // IE specific code path to prevent textarea
    // being shown while dialog is visible.
    return window.clipboardData.setData('Text', text)
  } else if (
    document.queryCommandSupported &&
    document.queryCommandSupported('copy')
  ) {
    const textarea = document.createElement('textarea')
    textarea.textContent = text
    // Prevent scrolling to bottom of page in MS Edge.
    textarea.style.position = 'fixed'
    document.body.appendChild(textarea)
    textarea.select()

    try {
      // Security exception may be thrown by some browsers.
      return document.execCommand('copy')
    } catch (ex) {
      console.warn('Copy to clipboard failed.', ex)
      return false
    } finally {
      document.body.removeChild(textarea)
    }
  }
}

const sortReflections = (assignments) => {
  return [...assignments].sort(
    (a, b) =>
      dateFromISO8601(a.answer.submittedAt) -
      dateFromISO8601(b.answer.submittedAt)
  )
}

function Reflection({ id, shareURL, currentUserPartial }) {
  const [activeAssignmentId, setActiveAssignmentId] = React.useState(null)
  const [quoteOpacity, setQuoteOpacity] = React.useState(1)
  const [shareCopied, setShareCopied] = React.useState(false)
  const [saving, setSaving] = React.useState(false)
  const [initialLoad, setInitialLoad] = React.useState(true)
  const [exportModal, setExportModal] = React.useState(false)
  const [exportList, setExportList] = React.useState([])
  const [additionalThoughtsOpen, setAdditionalThoughtsOpen] =
    React.useState(false)
  const [randomQuote, setRandomQuote] = React.useState('')
  const [additionalQuestion, setAdditionalQuestion] = React.useState(null)

  const { error, loading, data, refetch } = useQuery(fetchReflection, {
    variables: { id: id },
  })

  if (loading) return <div>Loading...</div>
  if (error) console.log(error) //return <div>Error :(</div>

  // React.useEffect(() => {
  //   const formId = window.location.hash.split('#')[1]
  //
  //   if (formId && initialLoad) {
  //     const assignmentId = window.location.hash.split('-')[1]
  //     setInitialLoad(false)
  //     setActiveAssignmentId(assignmentId)
  //
  //     // Using a timeout to ensure the state updates before attempting to scroll
  //     setTimeout(() => {
  //       const form = document.getElementById(formId)
  //       if (form) {
  //         form.scrollIntoView({ inline: 'start' })
  //       }
  //     }, 0)
  //   }
  // }, [initialLoad])

  const toggleAdditionalThoughts = (assignment) => {
    if (additionalThoughtsOpen) {
      refetch()
    }
    setAdditionalThoughtsOpen(!additionalThoughtsOpen)
    setAdditionalQuestion(assignment)
  }

  const openExportModal = (e) => {
    const ids = data?.project?.completedAssignments
      .filter((a) => !a.metaReflection)
      .map((i) => i.id)
    e.preventDefault()
    setExportModal(true)
    setExportList(ids)
  }

  const handleExport = (e) => {
    e.preventDefault()

    const base = 'https://refleq-export.herokuapp.com/api/render'
    const options = [
      'emulateScreenMedia=false',
      'pdf.margin.top=1cm',
      'pdf.margin.bottom=1cm',
      'pdf.margin.left=1cm',
      'pdf.margin.right=1cm',
      `attachmentName=Refleq${currentUserPartial}${id}.pdf`,
    ].join('&')
    const exportURL = `${base}?url=${shareURL}&${options}&ids=${exportList.join(
      '|'
    )}`
    window.open(exportURL)
    setExportModal(false)
  }

  const handlePrint = (e) => {
    e.preventDefault()

    window.print()
  }

  const handleShare = (e) => {
    e.preventDefault()

    copyToClipboard(shareURL)

    setShareCopied(true)
    setTimeout(() => {
      setShareCopied(false)
    }, 10000)
  }

  const handleShowQuote = () => {
    const header = document.getElementById('reflection')
    setTimeout(() => {
      header.scrollIntoView({ behavior: 'smooth' })
    }, 300)
    setTimeout(() => {
      setRandomQuote(data.randomQuote)
    }, 800)
  }

  const handleSave = (callback = () => {}) => {
    setSaving(true)
    setRandomQuote(null)
    setTimeout(() => {
      setSaving(false, callback)
    }, 3000)
  }

  const setActiveAssignment = (id) => {
    setActiveAssignmentId(id)
  }

  const saveAndExit = () => {
    handleSave(() => {
      window.location.href = '/'
    })
  }

  const handleClose = () => {
    setExportModal(false)
  }

  const toggleChecked = (id) => {
    let exists = exportList.find((i) => {
      return i === id
    })
    let tempExportList
    if (exists) {
      tempExportList = exportList.filter((val) => val !== id)
    } else {
      tempExportList.push(id)
    }
    setExportList(tempExportList)
  }

  const isChecked = (id) => {
    if (
      exportList.find((i) => {
        return i === id
      })
    )
      return true
    return false
  }

  const groupAssignments = (assignments) => {
    let container = []

    let currentGroup = []

    let currentType = null
    assignments.forEach((element) => {
      if (currentType === null) {
        currentGroup.push(element)
        currentType = element.metaReflection
      } else {
        if (element.metaReflection === currentType) {
          currentGroup.push(element)
        } else {
          currentType = element.metaReflection
          container.push(clone(currentGroup))
          currentGroup = []
          currentGroup.push(element)
        }
      }
    })
    container.push(currentGroup)
    return container
  }

  if (!data) return null

  const { completedAssignments, outstandingAssignments } = data.project
  window.outstandingAssignments = outstandingAssignments
  const assignments = outstandingAssignments.concat(completedAssignments)

  const mods =
    assignments &&
    assignments.map((a) => a.answer && dateFromISO8601(a.answer.updatedAt))
  const lastModified = Math.max(...mods)

  if (!activeAssignmentId && outstandingAssignments[0]) {
    setActiveAssignmentId(outstandingAssignments[0].id)
  }

  const shareCopiedClasses = shareCopied
    ? 'share-copied active'
    : 'share-copied'
  const exportable = sortReflections(
    completedAssignments.filter((a) => !a.metaReflection)
  )
  const groupedAssignments = groupAssignments(
    sortReflections(completedAssignments)
  )
  return (
    <div className="reflection">
      {(randomQuote ||
        (outstandingAssignments && outstandingAssignments.length > 0)) && (
        <StickyContainer className="time-to-reflect">
          <Sticky topOffset={40} bottomOffset={40}>
            {({ isSticky, style }) => {
              const headingClass = isSticky ? 'sticky' : ''
              return (
                <h2 className={headingClass} style={style}>
                  Time to reflect!
                  <div className="save-info">
                    {saving ? (
                      <div className="save-indicator">
                        <Spinner />
                        <span>Saving...</span>
                      </div>
                    ) : (
                      <span className="last-modified">
                        {lastModified
                          ? `Last edited ${formattedTime(lastModified)}`
                          : ''}
                      </span>
                    )}
                    <button onClick={saveAndExit}>Save And Exit</button>
                  </div>
                </h2>
              )
            }}
          </Sticky>
          {randomQuote && <Quote quote={randomQuote} opacity={quoteOpacity} />}
          {outstandingAssignments &&
            sort(byDueAtAsc, outstandingAssignments).map((a) => (
              <ReflectionForm
                id={data.project.id}
                active={a.id === activeAssignmentId}
                data={a}
                handleFocus={setActiveAssignment}
                key={a.id}
                onSave={handleSave}
                onSuccess={handleShowQuote}
              />
            ))}
        </StickyContainer>
      )}
      <div className="completed-reflections">
        {completedAssignments.length > 0 && (
          <StickyContainer>
            <Sticky>
              {({ isSticky, style }) => {
                const headingClass = isSticky ? 'sticky' : ''
                return (
                  <h2 className={headingClass} style={style}>
                    Reflection
                    <div className="actions">
                      <a onClick={handlePrint}>
                        <i className="material-icons">local_printshop</i>
                        Print
                      </a>
                      <a onClick={openExportModal}>
                        <i className="material-icons">picture_as_pdf</i>
                        Export
                      </a>
                      <a onClick={handleShare}>
                        <div className={shareCopiedClasses}>
                          <i className="material-icons">check_circle</i>
                          <span
                            onClick={() => {
                              window.open(shareURL, '_blank')
                            }}
                          >
                            Public link copied!
                          </span>
                        </div>
                        <i className="material-icons">reply</i>
                        Share
                      </a>
                    </div>
                  </h2>
                )
              }}
            </Sticky>
            {groupedAssignments && (
              <div>
                {groupedAssignments.map((group) => (
                  <div className="completed-assignments">
                    {group.map((a) => (
                      <div className="assignment" key={a.id}>
                        {a.metaReflection && (
                          <label className="meta-reflection">
                            Meta-Reflection
                          </label>
                        )}
                        <div className="submitted-at">
                          {a.answer.submittedAt > a.dueAt && (
                            <div className="badge">Late</div>
                          )}
                          Submitted {formattedTime(a.answer.submittedAt)}
                        </div>
                        <label>{a.question.text}</label>
                        <div className="text">
                          {a.answer.text}
                          <PostscriptText answer={a.answer} />
                        </div>
                        {!a.answer.postscript && (
                          <div class="subheader">
                            <a onClick={() => toggleAdditionalThoughts(a)}>
                              <i class="material-icons">add_circle</i>
                              Add more
                            </a>
                          </div>
                        )}
                        {a.answer.evidence && (
                          <a className="evidence" href={a.answer.evidence}>
                            <i className="material-icons">attach_file</i>
                            {decodeURI(
                              a.answer.evidence
                                .split('/')
                                .pop()
                                .replace(/\?.+/, '')
                            )}
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </StickyContainer>
        )}
      </div>
      <JournalExportModal
        exportModal={exportModal}
        handleClose={handleClose}
        exportable={exportable}
        exportList={exportList}
        handleExport={handleExport}
        toggleChecked={toggleChecked}
        isChecked={isChecked}
      />
      {additionalQuestion && (
        <AdditionalThoughtsModal
          isOpen={additionalThoughtsOpen}
          assignment={additionalQuestion}
          id={10}
          onClose={toggleAdditionalThoughts}
        />
      )}
    </div>
  )
}

const reflection = document.getElementById('reflection')
if (reflection) {
  const id = reflection.getAttribute('data-id')
  const shareURL = reflection.getAttribute('data-share-url')
  const currentUserPartial = reflection.getAttribute('data-current-user')

  const root = createRoot(reflection)
  root.render(
    <ApolloProvider client={client}>
      <Reflection
        id={id}
        shareURL={shareURL}
        currentUserPartial={currentUserPartial}
      />
    </ApolloProvider>
  )
}
